import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Tag as TagIcon, Loader2, Check, Info } from "lucide-react";
import { SkeletonList } from "@/components/admin/Skeleton";

const EVENTS = [
  { key: "contact", label: "New Contact-form Enquiry", hint: "Someone submits the contact form on the landing page." },
  { key: "wizard", label: "New AI-Wizard Lead", hint: "Someone completes the Get-Started wizard and gets a proposal." },
  { key: "order", label: "New Client Order", hint: "A logged-in client places an order from the client portal." },
  { key: "signup", label: "New Client Signup", hint: "Someone creates a new client-portal account." },
];

export default function LeadRoutingPanel({ API, getAuthHeaders }) {
  const [routing, setRouting] = useState(null);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingEvent, setSavingEvent] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r, t] = await Promise.all([
        axios.get(`${API}/admin/lead-routing`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/role-tags`, { headers: getAuthHeaders() }),
      ]);
      setRouting(r.data);
      setTags(t.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load lead routing");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleTagForEvent = async (event, tagId) => {
    const current = routing[event]?.tag_ids || [];
    const next = current.includes(tagId) ? current.filter((x) => x !== tagId) : [...current, tagId];
    setSavingEvent(event);
    try {
      const res = await axios.put(
        `${API}/admin/lead-routing/${event}`,
        { tag_ids: next },
        { headers: getAuthHeaders() }
      );
      setRouting((prev) => ({ ...prev, [event]: res.data }));
      toast.success(`Updated ${event} routing`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setSavingEvent(null);
    }
  };

  if (loading || !routing) return <SkeletonList count={4} />;

  return (
    <div className="space-y-6" data-testid="lead-routing-panel">
      <div className="border border-white/10 bg-[#0E0E0E] p-5">
        <div className="flex items-start gap-3">
          <Info size={16} className="text-red-500 shrink-0 mt-0.5" />
          <div className="text-xs text-zinc-400 leading-relaxed">
            When an event fires, every active staff member carrying <strong className="text-white">any</strong> of the selected tags
            will receive the alert email. The primary admin address is always included as a fallback so leads are never lost.
            Manage the tag list itself from the <strong className="text-white">Team</strong> tab.
          </div>
        </div>
      </div>

      {tags.length === 0 && (
        <div className="border border-amber-600/40 bg-amber-950/20 p-5 text-xs text-amber-300">
          No role tags exist yet. Go to <strong>Team → Manage Tags</strong> to create tags like "CFO" or "Sales"
          and assign them to staff — then return here to wire the events.
        </div>
      )}

      <div className="grid gap-4">
        {EVENTS.map((ev) => {
          const info = routing[ev.key] || { tag_ids: [], tags: [], recipients_preview: [] };
          const selected = info.tag_ids || [];
          return (
            <div
              key={ev.key}
              data-testid={`lead-routing-${ev.key}`}
              className="border border-white/10 bg-[#0E0E0E] p-5 sm:p-6"
            >
              <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                <div className="min-w-0">
                  <h3 className="text-sm font-bold uppercase tracking-[0.18em] text-white flex items-center gap-2">
                    <Mail size={14} className="text-red-500" /> {ev.label}
                  </h3>
                  <p className="text-[11px] text-zinc-500 mt-1">{ev.hint}</p>
                </div>
                {savingEvent === ev.key && (
                  <span className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                    <Loader2 size={11} className="animate-spin" /> Saving
                  </span>
                )}
              </div>

              <div className="mt-4">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2 flex items-center gap-1">
                  <TagIcon size={10} /> Alert staff tagged as
                </p>
                {tags.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {tags.map((t) => {
                      const on = selected.includes(t.id);
                      return (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => toggleTagForEvent(ev.key, t.id)}
                          disabled={savingEvent === ev.key}
                          data-testid={`lead-routing-${ev.key}-tag-${t.id}`}
                          className={`tap text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 border transition-all flex items-center gap-1.5 ${
                            on
                              ? "text-white border-transparent"
                              : "text-zinc-400 border-white/15 hover:border-white/40 hover:text-white"
                          }`}
                          style={on ? { background: t.color, borderColor: t.color } : undefined}
                        >
                          {on && <Check size={10} />} {t.name}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-[11px] text-zinc-600 italic">No tags created yet.</p>
                )}
              </div>

              <div className="mt-4 pt-4 border-t border-white/5">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">
                  Will be emailed to ({info.recipients_preview.length})
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {info.recipients_preview.map((email) => (
                    <span
                      key={email}
                      className="text-[10px] text-zinc-300 bg-[#1a1a1a] border border-white/10 px-2 py-1 font-mono"
                    >
                      {email}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
